import { useEffect, useMemo, useState } from 'react';
import { toastSuccess, toastWarning } from '../components/popups/Toast';
import { makeCustomGroupsApi } from './customGroupsApi';
import { applySaveGroupsOps, buildFundIdLookup, diffGroups, toFrontendGroup } from './customGroupsMapper';
import { filterContentForSync } from './customGroupsHelpers';

// Owns the groups list + CRUD + Linked-mode content sync. Selection /
// pool state stays in the selector; this hook takes whatever refs and
// setters it needs as inputs, so the data flow remains explicit.
//
// Inputs:
//   remote, portfolios, template
//   onGroupsChange(groups)
//   pendingRef, readyRef       — pool snapshots
//   videoModes, setVideoModes  — for syncGroupContent post-write
//   selectedGroup, selectedGroupStatusRef, isGroupMemberPreviewRef
//   setSelectedFund, setSelectedGroup
//   skipFundEffectRef           — gate the selectedFund effect during programmatic snap
//   setPortfolioMode            — push selection up to BuilderPage
//
// Returns:
//   { groups, groupsLoaded, customGroupsApi, idLookup,
//     saveGroups, deleteGroupById, toggleGroupLinked, commitRename,
//     syncGroupContent }
export function useCustomGroups({
    remote, portfolios, template,
    onGroupsChange,
    pendingRef, readyRef,
    videoModes, setVideoModes,
    selectedGroup, selectedGroupStatusRef, isGroupMemberPreviewRef,
    setSelectedFund, setSelectedGroup,
    skipFundEffectRef,
    setPortfolioMode,
}) {
    const [groups, setGroups] = useState([]);
    const [groupsLoaded, setGroupsLoaded] = useState(false);

    const customGroupsApi = useMemo(() => makeCustomGroupsApi(remote), [remote]);
    const idLookup = useMemo(() => buildFundIdLookup(portfolios), [portfolios]);

    // Load groups once a portfolios lookup is available. groupsLoaded flips
    // after the request settles so the URL-bridge in the caller can wait
    // for the real groups list before classifying a deep-link ticker.
    useEffect(() => {
        if (!portfolios || portfolios.length === 0) return;
        customGroupsApi.list()
            .then(dtos => {
                const mapped = (dtos || []).map(dto => toFrontendGroup(dto, idLookup));
                setGroups(mapped);
                if (onGroupsChange) onGroupsChange(mapped);
            })
            .catch(() => toastWarning('Could not load portfolio groups'))
            .finally(() => setGroupsLoaded(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolios]);

    // Copy primary's content to other members. Best-effort; failures
    // surface as a toast and the membership/mode change still stands.
    // Excludes managerComplete (per-fund Pending/Ready status — must not propagate).
    async function syncGroupContent(group, members) {
        if (!group || group.linked === false || !members || members.length <= 1) return;
        const primary = members[0];
        const others = members.slice(1);
        if (others.length === 0) return;
        try {
            const primaryData = await remote.get(`retrievev2/${btoa(primary.fundNumber)}/${template}`);
            const payload = filterContentForSync(primaryData);
            // contentType is an implicit 'editor' default when never toggled, so
            // the primary's blob may lack the key. Force it in so a member
            // joining is always flipped to the primary's mode — otherwise the
            // member keeps its own stale contentType (e.g. a video row staying
            // video inside a text group).
            if (typeof payload.contentType !== 'string') {
                payload.contentType = videoModes[primary.fundNumber] || 'editor';
            }
            await remote.put(`savev2-bulk/${template}`, {
                tickers: others.map(p => p.fundNumber),
                data: payload,
            });
            setVideoModes(prev => {
                const updated = { ...prev };
                for (const p of others) updated[p.fundNumber] = payload.contentType;
                return updated;
            });
        } catch (e) {
            console.error('syncGroupContent failed', e);
            toastWarning(`Could not sync content for "${group.name}". Members may be out of sync.`);
        }
    }

    async function reconcileGroupContentType(group) {
        const pool = selectedGroupStatusRef.current === 'ready' ? readyRef.current : pendingRef.current;
        const members = pool.filter(p => group.fundNumbers.includes(p.fundNumber));
        if (members.length <= 1) return;
        const primary = members[0];
        const canonical = videoModes[primary.fundNumber] || 'editor';
        const divergent = members.slice(1).filter(p => (videoModes[p.fundNumber] || 'editor') !== canonical);
        if (divergent.length === 0) return;
        try {
            await remote.put(`savev2-bulk/${template}`, {
                tickers: divergent.map(p => p.fundNumber),
                data: { contentType: canonical },
            });
            setVideoModes(prev => {
                const updated = { ...prev };
                for (const p of divergent) updated[p.fundNumber] = canonical;
                return updated;
            });
            toastSuccess(`Linked edits: aligned content type for ${divergent.length} portfolio${divergent.length > 1 ? 's' : ''}.`);
        } catch (e) {
            console.error('reconcileGroupContentType failed', e);
            toastWarning('Could not align content types across the group');
        }
    }

    function snapToFirstMember(group) {
        const pool = selectedGroupStatusRef.current === 'ready' ? readyRef.current : pendingRef.current;
        const members = pool.filter(p => group.fundNumbers.includes(p.fundNumber));
        const primary = members[0];
        if (!primary) return;
        skipFundEffectRef.current = true;
        isGroupMemberPreviewRef.current = false;
        setSelectedFund(primary);
        setSelectedGroup(group);
        if (setPortfolioMode) {
            const mode = videoModes[primary.fundNumber] || 'editor';
            setPortfolioMode({
                mode,
                fundNumber: primary.fundNumber,
                name: group.name,
                members,
                status: selectedGroupStatusRef.current || 'pending',
                isMemberPreview: false,
                linked: true,
            });
        }
    }

    async function saveGroups(newGroups) {
        const ops = diffGroups(groups, newGroups);
        const idFor = (fundNumber) => idLookup.byFundNumber.get(fundNumber);
        try {
            const { createdIds } = await applySaveGroupsOps(ops, customGroupsApi, idFor);
            const fresh = (await customGroupsApi.list()).map(dto => toFrontendGroup(dto, idLookup));
            setGroups(fresh);
            if (onGroupsChange) onGroupsChange(fresh);

            // Post-loop: sync content into any Linked group that just gained
            // members so the newcomers inherit the group's existing content.
            // Brand-new groups sync too — their members only appear in
            // ops.creates, never ops.memberAdds; the first pool member is the
            // content source (same rule as toggleGroupLinked's Individual →
            // Linked sync). Marked with `null` instead of a fundNumber list.
            const addsByGroup = new Map();
            for (const a of ops.memberAdds) {
                if (!addsByGroup.has(a.id)) addsByGroup.set(a.id, []);
                addsByGroup.get(a.id).push(a.fundNumber);
            }
            for (const id of createdIds.values()) addsByGroup.set(id, null);
            for (const [groupId, addedFundNumbers] of addsByGroup) {
                const group = fresh.find(g => g.id === groupId);
                if (!group || group.linked === false) continue;
                const allMembers = [...pendingRef.current, ...readyRef.current]
                    .filter(p => group.fundNumbers.includes(p.fundNumber));
                const primary = allMembers[0];
                if (!primary) continue;
                const isNewGroup = addedFundNumbers === null;
                // Existing group whose primary itself is a newcomer: its content
                // isn't the group's established content, so don't propagate it.
                if (!isNewGroup && addedFundNumbers.includes(primary.fundNumber)) continue;
                const newMemberObjs = isNewGroup
                    ? allMembers.slice(1)
                    : allMembers.filter(p => addedFundNumbers.includes(p.fundNumber));
                if (newMemberObjs.length === 0) continue;
                await syncGroupContent(group, [primary, ...newMemberObjs]);
            }

            toastSuccess('Groups saved');
            return { ok: true, groups: fresh };
        } catch (err) {
            console.error('saveGroups failed', err);
            toastWarning('Could not save groups. Please try again.');
            return { ok: false, error: err };
        }
    }

    function deleteGroupById(id) {
        customGroupsApi.deleteGroup(id)
            .then(() => {
                const newGroups = groups.filter(g => g.id !== id);
                setGroups(newGroups);
                if (onGroupsChange) onGroupsChange(newGroups);
                if (selectedGroup?.id === id) {
                    setSelectedGroup(null);
                    setSelectedFund(null);
                }
            })
            .catch((err) => {
                console.error('deleteGroup failed', err);
                toastWarning('Could not delete group');
            });
    }

    async function toggleGroupLinked(id, value) {
        let dto;
        try {
            dto = await customGroupsApi.update(id, { editMode: value === false ? 'INDIVIDUAL' : 'GROUP' });
        } catch (e) {
            console.error('toggleGroupLinked failed', e);
            toastWarning('Could not change editing mode');
            return;
        }
        const mapped = toFrontendGroup(dto, idLookup);
        const next = groups.map(g => g.id === id ? { ...g, linked: mapped.linked } : g);
        setGroups(next);
        if (onGroupsChange) onGroupsChange(next);
        // Data-consistency guards on Individual → Linked transitions
        if (value === true) {
            const group = next.find(g => g.id === id);
            if (group) {
                await reconcileGroupContentType(group);
                const pool = selectedGroupStatusRef.current === 'ready' ? readyRef.current : pendingRef.current;
                const members = pool.filter(p => group.fundNumbers.includes(p.fundNumber));
                await syncGroupContent(group, members);
                snapToFirstMember(group);
            }
        }
    }

    function commitRename(id, trimmedName, onDone) {
        const trimmed = (trimmedName || '').trim();
        if (!trimmed) { if (onDone) onDone(); return; }
        customGroupsApi.update(id, { customGroupName: trimmed })
            .then(dto => {
                const mapped = toFrontendGroup(dto, idLookup);
                const next = groups.map(g => g.id === id ? { ...g, name: mapped.name } : g);
                setGroups(next);
                if (onGroupsChange) onGroupsChange(next);
            })
            .catch((err) => {
                console.error('commitRename failed', err);
                toastWarning('Could not rename group');
            })
            .finally(() => { if (onDone) onDone(); });
    }

    return {
        groups, groupsLoaded,
        customGroupsApi, idLookup,
        saveGroups, deleteGroupById, toggleGroupLinked, commitRename,
        syncGroupContent,
    };
}
